import {
  Box,
  InputLabel,
  BaseTextFieldProps,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import React from "react";
import {
  CountryIso2,
  defaultCountries,
  FlagEmoji,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import type { ResponsiveStyleValue } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import CustomInput from "global/components/CustomInput/CustomInput";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import strings from "global/constants/StringConstants";
import customContactNumberStyles from "./CustomContactNumberInput.styles";

export interface MUIPhoneProps extends BaseTextFieldProps {
  label?: string;
  value: string;
  placeHolder?: string;
  error?: any;
  flagDisabled?: boolean;
  id?: string;
  onChange: (phone: string) => void;
  InputProps?: any;
  inputProps?: any;
  /** Style overrides for the label. */
  labelSx?: SxProps<Theme>;
  /**
   * Gap between the label and the field (MUI spacing units or a CSS length).
   * Takes a breakpoint map too, so a screen can tighten it on small viewports.
   */
  labelSpacing?: ResponsiveStyleValue<number | string>;
  /**
   * Restrict the country selector to these ISO-3166 alpha-2 codes (lowercase,
   * e.g. `["us", "ca"]`). Omit for the full list, which is the default
   * everywhere — this is opt-in per call site, because the restriction is a
   * business rule about one flow, not a property of the control.
   *
   * Applied to the picker *and* to `usePhoneInput`, so a number pasted with an
   * out-of-list dial code can't select a country the menu doesn't offer.
   * An empty or unrecognised list falls back to the full set rather than
   * rendering a picker with nothing in it.
   */
  countries?: CountryIso2[];
}

/**
 * Merges a caller override on top of a base style rather than replacing it —
 * a bare `sx={override}` would otherwise drop the field's own chrome (box
 * height, border, focus ring) entirely.
 */
const mergeSx = (
  base: SxProps<Theme>,
  override?: SxProps<Theme>,
): SxProps<Theme> =>
  [base, ...(Array.isArray(override) ? override : [override])] as SxProps<Theme>;

/** Below this many countries the menu fits on screen and needs no search. */
const COUNTRY_SEARCH_MIN_OPTIONS = 10;

/**
 * Keys the search box hands on to the menu: arrows move into the list, Escape
 * and Tab close it. Every other key stays in the box — otherwise the menu's
 * type-ahead would steal each letter and jump focus to a matching country.
 */
const MENU_NAVIGATION_KEYS = ["ArrowDown", "ArrowUp", "Escape", "Tab"];

/** Matches a country by name, ISO code ("in") or dial code ("+91" / "91"). */
const matchesCountrySearch = (
  country: ReturnType<typeof parseCountry>,
  term: string,
) => {
  const query = term.trim().toLowerCase();
  if (!query) return true;
  const digits = query.replace(/^\+/, "");
  return (
    country.name.toLowerCase().includes(query) ||
    country.iso2 === query ||
    (/^\d+$/.test(digits) && country.dialCode.startsWith(digits))
  );
};

/**
 * The app's phone-number field: one text input with the country's flag as a
 * `Select` adornment. `usePhoneInput` (react-international-phone) tracks the
 * international value; this component only owns presentation.
 */
export const CustomContactNumberInput: React.FC<MUIPhoneProps> = (props) => {
  const {
    label,
    value,
    placeHolder,
    error,
    onChange,
    required,
    variant,
    flagDisabled,
    id,
    disabled,
    labelSx,
    labelSpacing,
    countries,
    sx,
    InputProps,
    inputProps,
    helperText,
    ...restProps
  } = props;

  const classes = customContactNumberStyles;

  /**
   * The country set this instance offers. Filtering `defaultCountries` (rather
   * than hand-building entries) keeps each country's dial code, mask and
   * priority exactly as the library defines them.
   */
  const countryList = React.useMemo(() => {
    if (!countries?.length) return defaultCountries;
    const allowed = new Set(countries.map((iso2) => iso2.toLowerCase()));
    const filtered = defaultCountries.filter((countryData) =>
      allowed.has(parseCountry(countryData).iso2),
    );
    // Never leave the picker empty — a typo in the caller's list would
    // otherwise strand the user with no country to choose.
    return filtered.length ? filtered : defaultCountries;
  }, [countries]);

  const { phone, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      // Stays "us" (the app-wide default) whenever the list still offers it,
      // and only falls back to whatever is first when a caller has excluded it
      // — a default the picker can't render would leave the flag blank.
      defaultCountry: countryList.some(
        (countryData) => parseCountry(countryData).iso2 === "us",
      )
        ? "us"
        : parseCountry(countryList[0]).iso2,
      value,
      countries: countryList,
      onChange: (data: any) => {
        onChange(data.phone);
      },
    });

  const [countrySearch, setCountrySearch] = React.useState("");
  const showCountrySearch = countryList.length > COUNTRY_SEARCH_MIN_OPTIONS;

  const parsedCountries = React.useMemo(
    () => countryList.map((countryData) => parseCountry(countryData)),
    [countryList],
  );
  const hasCountryMatch = parsedCountries.some((parsed) =>
    matchesCountrySearch(parsed, countrySearch),
  );

  const inputId = id || "contactNumber";
  const countrySelectLabelId = `${inputId}-country-code-label`;

  return (
    <Box
      width="100%"
      sx={(theme) => ({
        borderBottom:
          variant === "standard"
            ? `1px solid ${theme.palette.divider}`
            : "none",
        pb: variant === "standard" ? 0.5 : 0,
      })}
    >
      {label && (
        <Box mb={labelSpacing ?? formFieldMetrics.labelGap}>
          <InputLabel
            id={countrySelectLabelId}
            htmlFor={inputId}
            sx={mergeSx(classes.inputLabel, labelSx)}
            required={required}
          >
            {label}
          </InputLabel>
        </Box>
      )}
      <TextField
        {...restProps}
        id={inputId}
        sx={mergeSx(classes.textField, sx)}
        variant={variant ? variant : "outlined"}
        fullWidth
        disabled={disabled}
        value={phone}
        placeholder={placeHolder}
        onChange={handlePhoneValueChange}
        type="tel"
        inputRef={inputRef}
        helperText={helperText}
        {...(error && { error: true, helperText: error })}
        FormHelperTextProps={{ role: error ? "alert" : undefined }}
        inputProps={{
          "aria-invalid": Boolean(error),
          ...inputProps,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={classes.adornment}>
              <Select
                aria-label="Country code selector"
                labelId={countrySelectLabelId}
                disabled={disabled}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      width: 360,
                      maxWidth: "calc(100vw - 32px)",
                    },
                  },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  // Focus goes to the search box instead of the selected row.
                  ...(showCountrySearch && {
                    autoFocus: false,
                    MenuListProps: { sx: classes.countryMenuList },
                  }),
                }}
                sx={classes.countrySelect}
                value={country}
                onOpen={() => setCountrySearch("")}
                onChange={(e) => setCountry(e.target.value as CountryIso2)}
                renderValue={(value) => (
                  // `react-international-phone`'s own CSS never loads (its
                  // FlagEmoji.style.scss needs a Sass compiler this app
                  // doesn't have), so the flag's size must be set explicitly
                  // — left to the browser it renders at the raw SVG's native
                  // size, which overruns the space reserved for the arrow.
                  <FlagEmoji
                    iso2={value}
                    style={{ width: 20, height: 20, display: "block" }}
                  />
                )}
              >
                {showCountrySearch && (
                  // No tabindex, so Select ignores clicks on it rather than
                  // treating it as an option.
                  <ListSubheader sx={classes.countrySearchHeader}>
                    <CustomInput
                      autoFocus
                      name="countrySearch"
                      autoComplete="off"
                      value={countrySearch}
                      placeHolder={strings.countrySearchPlaceholder}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCountrySearch(e.target.value)
                      }
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (!MENU_NAVIGATION_KEYS.includes(e.key)) {
                          e.stopPropagation();
                        }
                      }}
                      propsToInputElement={{
                        "aria-label": strings.countrySearchPlaceholder,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={classes.countrySearchIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                )}
                {showCountrySearch && !hasCountryMatch && (
                  <ListSubheader sx={classes.countrySearchEmpty}>
                    {strings.countrySearchNoResults}
                  </ListSubheader>
                )}
                {parsedCountries.map((parsed) => {
                  const isFilteredOut = !matchesCountrySearch(
                    parsed,
                    countrySearch,
                  );
                  // The selected country stays mounted while filtered out, or
                  // Select would lose its value; hidden and disabled so arrow
                  // keys skip it.
                  if (isFilteredOut && parsed.iso2 !== country) return null;
                  return (
                    <MenuItem
                      key={parsed.iso2}
                      value={parsed.iso2}
                      sx={
                        isFilteredOut
                          ? classes.countryOptionHidden
                          : classes.countryOption
                      }
                      disabled={isFilteredOut || (flagDisabled ?? false)}
                    >
                      <FlagEmoji
                        iso2={parsed.iso2}
                        style={{ height: 20, width: 20, flexShrink: 0 }}
                      />
                      <Box component="span" sx={classes.countryName}>
                        {parsed.name}
                      </Box>
                      <Box component="span" sx={classes.countryDialCode}>
                        +{parsed.dialCode}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
          ...InputProps,
        }}
      />
    </Box>
  );
};

export default CustomContactNumberInput;
